'use client';
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useChatKeybindings } from './useChatKeybindings';
import { useConversationNaming } from './useConversationNaming';
import { useBrainChat, useBrainChatStatus } from './BrainChatProvider';
import { createChatKeymap, chatKeyTarget, type WebChatAction } from './chatKeymap';
import { useSkin } from '../../lib/skinContext';
import { useTranslation } from '../../lib/i18n';
import { useToast } from '../../components/ui/Toast';
import { StatusMark } from '../../components/ui/StatusMark';
import { apiErrorMessage, elowenClient } from '../../lib/elowenClient';
import { useVoiceCallController } from '../voice/callController';
import type { SubagentState } from '../../lib/transcript';
import { AUTH_CLEARED_EVENT, authGeneration, subscribeAuthTransitions } from '../../lib/token';

/** One surface owns one keymap, including F9. No global browser shortcut changes outside its focus. */
export function ChatKeyboard({ surfaceRef, onTelemetry, onSearch }: {
  surfaceRef: RefObject<HTMLDivElement | null>;
  onTelemetry?: () => void;
  onSearch?: () => void;
}) {
  const chat = useBrainChat();
  const status = useBrainChatStatus();
  const naming = useConversationNaming();
  const { reasoning } = chat;
  const skin = useSkin();
  const voice = useVoiceCallController();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [pending, setPending] = useState(false);
  const { effective } = useChatKeybindings();
  const keymap = useMemo(() => effective ? createChatKeymap(setPending, effective) : null, [effective]);
  const keymapRef = useRef(keymap);
  keymapRef.current = keymap;
  const authFence = useRef({ generation: authGeneration(), ready: true });
  useEffect(() => {
    const off = subscribeAuthTransitions(transition => {
      if (transition.phase === 'start') { authFence.current.ready = false; keymapRef.current?.cancel(); }
      if (transition.phase === 'ready' && transition.refresh) authFence.current = { generation: authGeneration(), ready: true };
    });
    const clear = () => { authFence.current.ready = false; keymapRef.current?.cancel(); };
    window.addEventListener(AUTH_CLEARED_EVENT, clear);
    return () => { off(); window.removeEventListener(AUTH_CLEARED_EVENT, clear); };
  }, []);
  const siblings = useRef<SubagentState[]>([]);
  useEffect(() => { if (!status.readOnly) siblings.current = status.subagents; }, [status.readOnly, status.subagents]);
  useEffect(() => { keymap?.cancel(); }, [keymap, chat.activeSessionId]);

  const reasoningRequest = useRef<object | null>(null);
  useEffect(() => () => { reasoningRequest.current = null; }, [
    chat.activeSessionId, chat.currentModel, chat.provider, status.readOnly, status.childFocus, voice?.active,
  ]);
  const cycleReasoning = async (): Promise<void> => {
    if (reasoningRequest.current) return;
    const request = {};
    reasoningRequest.current = request;
    try {
      // Reasoning is a command-time read, not another status subscription on every surface mount.
      const fresh = await elowenClient.brainStatus(chat.activeSessionId ?? undefined);
      if (reasoningRequest.current !== request) return;
      const levels = fresh.thinkingLevels ?? [];
      const current = fresh.thinkingLevel ?? '';
      const next = levels[(levels.indexOf(current) + 1) % levels.length];
      if (next) await reasoning.apply(next, current);
    } catch (error) {
      if (reasoningRequest.current === request) toast(apiErrorMessage(error), 'error');
    } finally {
      if (reasoningRequest.current === request) reasoningRequest.current = null;
    }
  };

  const run = (action: WebChatAction): void => {
    const editable = !status.readOnly && !status.childFocus && !voice?.active;
    // Mode, reasoning and model belong to the conversation's owner; a guest's chord says so instead.
    const ownerOnly = (): boolean => {
      if (status.guest) toast(t.chat.ownerOnlyControl, 'error');
      return !status.guest;
    };
    switch (action) {
      case 'stash':
        if (editable) chat.draft.stash();
        return;
      case 'mode_toggle':
        if (editable && ownerOnly()) chat.switchWorkMode(chat.workMode === 'build' ? 'plan' : 'build');
        return;
      case 'reasoning_cycle':
        if (editable && ownerOnly()) void cycleReasoning();
        return;
      case 'rewind': chat.rewind(); return;
      case 'rename':
        if (naming.editable && !status.guest && !voice?.active) chat.openRename();
        return;
      case 'stop': chat.abort(); return;
      case 'queue_steer': {
        if (!editable) return;
        // The daemon's per-viewer `canSteer` decides, exactly as the ↑ chip on the queue does.
        const next = status.queued.findLast((entry) => entry.canSteer);
        if (next) void chat.onQueueSteer(next.id);
        return;
      }
      case 'queue_remove': {
        if (!editable) return;
        const last = status.queued.findLast((entry) => entry.removable);
        if (last) chat.onQueueRemove(last.id);
        return;
      }
      case 'reconnect': chat.retryConnection(); return;
      case 'help': chat.openHelp('shortcuts'); return;
      case 'search': onSearch?.(); return;
      case 'sessions_picker': chat.openHistory(); return;
      case 'model_picker':
        if (ownerOnly()) { chat.setModelOpen(true); chat.loadModels(); }
        return;
      case 'theme_picker':
        if (skin.allowed.length > 1 && !skin.switching) void skin.cycle().catch(error => toast(apiErrorMessage(error), 'error'));
        return;
      case 'telemetry_toggle':
        if (onTelemetry) onTelemetry();
        else chat.setStatsOpen(!chat.statsOpen);
        return;
      case 'subagent_cycle': {
        if (status.readOnly && !status.childFocus) return;
        const agents = status.childFocus ? siblings.current : status.subagents;
        const index = status.childFocus ? agents.findIndex(agent => agent.sessionId === status.childFocus) : -1;
        const next = agents[index + 1];
        if (!next && !status.childFocus) return;
        // A child disables its textarea; keep keyboard focus on this surface, not document.body.
        surfaceRef.current?.focus({ preventScroll: true });
        if (next && next.sessionId !== status.childFocus) {
          void chat.focusSubagentSession(next.sessionId, next.name).catch(error => toast(apiErrorMessage(error), 'error'));
        } else if (status.childFocus) chat.exitReadOnly();
        return;
      }
      case 'subagent_stop': {
        if (status.readOnly && !status.childFocus) return;
        const newest = status.subagents.filter(agent => agent.status === 'running')
          .reduce<SubagentState | undefined>((latest, agent) => !latest || (agent.runStartedAt ?? (Date.parse(agent.startedAt ?? '') || 0)) >= (latest.runStartedAt ?? (Date.parse(latest.startedAt ?? '') || 0)) ? agent : latest, undefined);
        const target = status.childFocus ?? newest?.sessionId;
        if (target) void elowenClient.brainAbort(target).catch(error => toast(apiErrorMessage(error), 'error'));
        return;
      }
    }
  };
  const runRef = useRef(run);
  runRef.current = run;
  const searchRef = useRef(onSearch);
  searchRef.current = onSearch;

  useEffect(() => {
    if (!keymap) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!authFence.current.ready || authFence.current.generation !== authGeneration()) { keymap.cancel(); return; }
      const target = chatKeyTarget(event.target);
      if (!target || target.closest('[data-chat-keyboard-surface]') !== surfaceRef.current) { keymap.cancel(); return; }
      if (event.defaultPrevented) return;
      const composer = target instanceof HTMLTextAreaElement;
      if (!composer && event.key === '?' && !event.ctrlKey && !event.altKey && !event.metaKey && !event.shiftKey && !event.isComposing && !event.repeat) {
        keymap.cancel();
        event.preventDefault();
        event.stopPropagation();
        runRef.current('help');
        return;
      }
      if (!composer && event.key === 'Tab') return;
      const selected = composer ? target.selectionStart !== target.selectionEnd : !!window.getSelection()?.toString();
      const action = keymap.resolve(event, selected);
      if (!action || (action === 'mode_toggle' && !composer) || (action === 'search' && !searchRef.current)) return;
      event.preventDefault();
      event.stopPropagation();
      if (action !== 'leader' && action !== 'cancel') runRef.current(action);
    };
    const onFocus = (event: FocusEvent): void => {
      if (!chatKeyTarget(event.target) || !(event.target instanceof Node) || !surfaceRef.current?.contains(event.target)) keymap.cancel();
    };
    const cancel = () => keymap.cancel();
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('focusin', onFocus);
    window.addEventListener('blur', cancel);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('focusin', onFocus);
      window.removeEventListener('blur', cancel);
      keymap.cancel();
    };
  }, [keymap, surfaceRef]);

  useEffect(() => {
    if (!pending || !keymap) return;
    // A modal may mount before its focus trap moves focus; never carry a leader across it.
    const cancelOnModal = () => { if (document.querySelector('[role="dialog"][aria-modal="true"]')) keymap.cancel(); };
    const observer = new MutationObserver(cancelOnModal);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['role', 'aria-modal'] });
    cancelOnModal();
    return () => observer.disconnect();
  }, [pending, keymap]);

  return pending ? <span data-testid="chat-leader-pending"><span className="sr-only" role="status">{t.brainChat.leaderPending}</span><StatusMark kind="waiting" tone="muted" label={t.brainChat.leaderPending} /></span> : null;
}
